/* Fôringsdøgnet, som noe man TELLER.
 *
 * NEURATH, IKKE ET DIAGRAM
 *   Isotype-regelen: for å vise dobbelt så mye, bruk to symboler -- aldri ett
 *   symbol som er dobbelt så stort. Øyet teller godt og bedømmer areal dårlig.
 *   Så et fôringsdøgn er ikke en strek med en lengde man må sammenligne med
 *   naboen; det er en rekke merker man kan telle. Elleve merker er elleve timer.
 *
 *   Og et avbrudd er et HULL i rekken. Et manglende element i en regelmessig
 *   serie er noe et menneske ser umiddelbart, uten forklaring og uten farge --
 *   som er like greit, for designmanualen har rødt til nøyaktig tre ting og
 *   dette er ikke ett av dem.
 *
 * HVA SOM BLE SLETTET
 *   Nivåkurva. Den tegnet 1440 små svingninger tvers over døgnet og bar ingen
 *   beslutning: ingen operatør gjør noe annerledes fordi kurva rugget. Arntz
 *   tegnet ikke skygger. Den er borte.
 *
 * OPPLØSNING
 *   Kvarter i dagsvisninga, time i stabelen. Valgt slik at det man MÅ se er
 *   tellbart: et avbrudd på 24 minutter er to manglende merker, ikke en
 *   forskjell i lengde man skal måle. */

import { useEffect, useState } from 'react'

import { useInne, useRolig } from './Maaling'

/** Sekundet nå, oppdatert hvert kvartminutt.
 *
 *  Ikke en klokke: det eneste den brukes til er å vite hvor langt inn i det
 *  inneværende kvarteret vi er kommet, og det flytter seg 1/60 av et merke i
 *  minuttet. Femten sekunder er tett nok til at streken glir og sjelden nok
 *  til at ingenting arbeider mellom hver gang.
 *
 *  Den går også under `prefers-reduced-motion`. Å stanse den ville ikke fjernet
 *  en animasjon, den ville frosset et tall -- og lesere som slår av bevegelse
 *  ber om ro, ikke om feil informasjon. */
function useNaa(aktiv: boolean, ms = 15000): number {
  const [naa, setNaa] = useState(() => Date.now() / 1000)
  useEffect(() => {
    if (!aktiv) return
    const id = setInterval(() => setNaa(Date.now() / 1000), ms)
    return () => clearInterval(id)
  }, [aktiv, ms])
  return naa
}

export interface Okt { start: number; end: number; minutes?: number }
export interface Stopp { start: number; end: number; minutes: number }

export interface Baat {
  hour_epoch: number
  name: string | null
  closest_m: number | null
  max_sog_kn: number | null
  aqua_fleet: boolean | null
}

/** Klokkeslett i gårdens egen sone. Epoker inn, lokal tid ut -- alltid. */
export const klokke = (e: number) =>
  new Date(e * 1000).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })

/** Gårdens vante vindu: fra- og til-time, delt av ALLE rader.
 *
 *  Dette var feil før, og det var den feilen som gjorde listen uleselig: hver
 *  rad spente fra sin egen første til sin egen siste økt, så to rader med ulik
 *  start så like ut og aksen flyttet seg nedover siden. En dag som begynte en
 *  time seinere skal ha et TOMT MERKE til venstre -- det er hele poenget. */
export interface Vindu {
  fromH: number; toH: number
  startH: number | null; endH: number | null
  /** Soloppgang og solnedgang, lokal time som desimaltall. Sporet tegnes bare
   *  mellom disse: DAGSLYSET er vinduet fôring kan skje i, så radens utstrekning
   *  er potensialet og merkene er det som ble brukt av det. Null under
   *  midnattssol -- da er hele døgnet lys, og et klokkeslett ville vært funnet
   *  på. */
  sunriseH: number | null; sunsetH: number | null
}

/** Merkene for ett døgn på det felles vinduet.
 *
 *  Tre tilstander, ikke en gradient: av, redusert, full. Isotype-regelen er at
 *  leseren teller symboler og ikke kan bedømme areal, så en opasitetsrampe
 *  ville vært en mengde ingen kan lese tilbake. */
export type Merke = 0 | 1 | 2 | 3

function merker(okter: Okt[], timer: number[] | undefined,
                v: Vindu, stegMin: number, dagStart: number,
                baatTimer?: boolean[], minutter?: (number | null)[],
                uvisse?: boolean[]
               ): { s: Merke; lys: boolean; baat: boolean;
                    fyll: number | null; uvisst: boolean }[] {
  const n = Math.round(((v.toH - v.fromH) * 60) / stegMin)
  const ut: { s: Merke; lys: boolean; baat: boolean;
              fyll: number | null; uvisst: boolean }[] = []
  for (let i = 0; i < n; i++) {
    const minutt = v.fromH * 60 + i * stegMin
    const t0 = dagStart + minutt * 60
    const t1 = t0 + stegMin * 60
    const time = minutt / 60
    const lys = v.sunriseH === null || v.sunsetH === null
      ? true                                    // midnattssol: alt er dagslys
      : time + stegMin / 120 >= v.sunriseH && time + stegMin / 120 <= v.sunsetH
    let dekket = 0
    for (const o of okter) {
      dekket += Math.max(0, Math.min(t1, o.end) - Math.max(t0, o.start))
    }
    const h = Math.floor(time)
    const baat = !!(baatTimer && baatTimer[h])
    /* HØYDEN ER BLÅSETID.
       Detektoren måler nå hvor mange sekunder blåseren faktisk gikk i hvert
       minutt, så en time har en MENGDE og ikke bare en tilstand -- typisk
       40-50 % av timen, fordi maskinen pulser: omkring ti sekunder på, tretten
       av. Merket fylles nedenfra i det forholdet.

       `minutter` er indeksert i MERKETS eget steg -- timer i stabelen,
       kvarter i dagsstripa -- så den samme regnestykket gjelder begge, og
       stripa arver ingenting fra timen rundt seg. Kvartersverdiene kommer fra
       dagsendepunktets 1440 sekundtall, ikke fra en time delt på fire. */
    const slot = Math.round(minutt / stegMin)
    const fyll = minutter && minutter[slot] != null
      ? Math.max(0, Math.min(1, (minutter[slot] as number) / stegMin))
      : null
    /* «Målt, men ikke til å stole på.» Båndet hadde for lite spenn til å finne
       en pause i -- en arbeidsbåt langs merden, eller maskinen rett og slett
       av. Merket viser høyden det MÅLTE og skraverer den, framfor å bli tegnet
       fullt, som var det gamle svaret og det verst tenkelige: 17. august ble
       en ettermiddag med 1-14 minutter fôring tegnet som dagens fulleste. */
    const uvisst = !!(uvisse && uvisse[slot] === false && fyll !== null)
    // 3 = ikke målbart. Ikke en fjerde slags fôring, men fravær av svar.
    if (timer && timer[h] === 3) {
      ut.push({ s: 3, lys, baat, fyll: null, uvisst: false }); continue
    }
    const s: Merke = dekket < (t1 - t0) * 0.5
      ? 0 : timer && timer[h] === 1 ? 1 : 2
    /* «PÅ, MEN UMÅLT» ER IKKE «PÅ HELE TIDA», og det har vært den samme feilen
       tre ganger nå: uten fylling faller merket tilbake på den heldekkende
       tilstandsflaten, og en heldekkende flate ER et fullt merke. Timer som
       ikke lot seg måle ble tegnet som dagens travleste; de tre siste
       kvarterene, der features ennå ikke har landet, ble tegnet som en maskin
       på full gass.

       Er dette et døgn som rapporterer blåsetid, og mangler nettopp dette
       merket et tall mens nivåsporet sier at det gikk, så er svaret fravær av
       svar -- og det har siden allerede et språk for: tomt merke med ramme.
       Er nivåsporet enig i at maskinen sto, blir merket stående som av: DET
       vet vi. */
    if (minutter && fyll === null && s > 0) {
      ut.push({ s: 3, lys, baat, fyll: null, uvisst: false }); continue
    }
    ut.push({ s, lys, baat, fyll, uvisst })
  }
  return ut
}

/** Midnatt lokal tid for et døgn, som epoke. Utledet fra øktene så vi slipper
 *  å sende det: en økt ligger alltid inne i sitt eget døgn. */
function dagStartAv(okter: Okt[], period: string): number {
  if (okter.length) {
    const d = new Date(okter[0]!.start * 1000)
    d.setHours(0, 0, 0, 0)
    return d.getTime() / 1000
  }
  const [y, mo, dd] = period.split('-').map(Number)
  return new Date(y!, mo! - 1, dd!, 0, 0, 0, 0).getTime() / 1000
}

/* --------------------------------------------------------------- én dag */

export function Dag({ okter, timer, stopp, baater, baatTimer, vindu, period,
                     minutter, uvisse, erIDag }: {
  okter: Okt[]; timer?: number[]; stopp: Stopp[]; baater: Baat[]
  baatTimer?: boolean[]; vindu: Vindu; period: string
  /** Minutter blåsetid per kvarter, 96 lange, null der sporet ikke rekker. */
  minutter?: (number | null)[]
  /** Om kvarteret lot seg måle. false skraverer merket. */
  uvisse?: boolean[]
  /** Døgnet som pågår. Bare da finnes det et kvarter som fylles nå. */
  erIDag?: boolean
}) {
  const inne = useInne([period])
  const rolig = useRolig()
  const start = dagStartAv(okter, period)
  const m = merker(okter, timer, vindu, 15, start, baatTimer, minutter, uvisse)
  /* KVARTERET SOM FYLLES NÅ.
     Det er halvferdig per definisjon, og det er derfor det trenger et eget
     merke framfor bare en animasjon: klokka 10:17 har kvarteret fra 10:15 hatt
     to av femten minutter på seg, så fyllingen KAN ikke bli mer enn 13 % uansett
     hvor hardt maskinen går. Uten en strek som sier hvor langt inn i kvarteret
     vi er, leses det siste merket som et fall i fôringen hver eneste gang.
     Streken er hvor langt kvarteret er kommet; fyllingen under den er hvor mye
     av det blåseren brukte. */
  const naaTid = useNaa(!!erIDag)
  const naaIdx = erIDag
    ? Math.floor((naaTid - start - vindu.fromH * 3600) / 900) : -1
  const forloept = naaIdx >= 0
    ? Math.min(1, Math.max(0, ((naaTid - start - vindu.fromH * 3600) % 900) / 900))
    : 0
  if (!m.length) return <p className="tom">Ingen fôring målt dette døgnet.</p>

  const naer = baater.filter((b) => (b.closest_m ?? 1e9) <= 120).length > 0
  const per = 60 / 15                                    // merker per time

  return (
    <div className={`dag-merker${naer ? ' baat' : ''}`}>
      <div className="merkerad" role="img"
           aria-label={`fôring ${okter.length ? klokke(okter[0]!.start) : '–'} til `
             + `${okter.length ? klokke(okter[okter.length - 1]!.end) : '–'}`
             + `${stopp.length ? `, ${stopp.length} avbrudd` : ', uten avbrudd'}`}>
        {m.map((x, i) => (
          <span key={i} className={`merke-kvart s${x.s}${x.lys ? '' : ' natt'}`
                  + `${x.baat ? ' baat' : ''}`
                  + `${x.fyll === null ? '' : ' maalt'}`
                  + `${x.uvisst ? ' uvisst' : ''}`
                  + `${i === naaIdx ? ' naa' : ''}`}
                style={{ transitionDelay: rolig ? undefined : `${Math.min(i * 7, 380)}ms`,
                         opacity: inne || rolig ? 1 : 0 }}>
            {x.fyll !== null && (
              <i style={{ height: `${Math.round(x.fyll * 100)}%` }} />
            )}
            {i === naaIdx && (
              <b style={{ bottom: `${Math.round(forloept * 100)}%` }}
                 aria-hidden="true" />
            )}
          </span>
        ))}
      </div>
      {/* Aksen er gårdens vante vindu, lik for alle døgn, med et hakk der den
          pleier å begynne og slutte. Det er DET som gjør «i dag begynte den
          seinere» synlig -- før flyttet aksen seg med dagen og skjulte akkurat
          den forskjellen. */}
      <div className="merker-akse">
        {Array.from({ length: vindu.toH - vindu.fromH + 1 }, (_, i) => {
          const h = vindu.fromH + i
          const vant = (vindu.startH !== null && Math.round(vindu.startH) === h)
            || (vindu.endH !== null && Math.round(vindu.endH) === h)
          return (
            <span key={h} className={vant ? 'vant' : undefined}
                  style={{ left: `${((h - vindu.fromH) / (vindu.toH - vindu.fromH)) * 100}%` }}>
              {String(h).padStart(2, '0')}
            </span>
          )
        })}
      </div>
      <div className="merker-per-time" aria-hidden="true">
        {Array.from({ length: vindu.toH - vindu.fromH }, (_, i) => (
          <i key={i} style={{ width: `${100 / (vindu.toH - vindu.fromH)}%` }} />
        ))}
      </div>
      {per > 0 && null}
    </div>
  )
}

/* ------------------------------------------------------- stabelen av døgn */

export interface DagRad {
  period: string
  okter: Okt[]
  timer?: number[]
  baatTimer?: boolean[]
  stopp: Stopp[]
  hours: number | null
  harBaat: boolean
  /** Anlegget ble arbeidet på. Ingen fôringstall, ikke et lavt fôringstall. */
  arbeid?: boolean
  /** Døgnet måles fortsatt. Raden er kortere fordi klokka ikke er tolv, ikke
   *  fordi det ble fôret mindre, og den sier det selv framfor å bli lest feil. */
  paagaar?: boolean
  /** Minutter blåsetid per time av døgnet, 24 lange. null for en time uten
   *  1 s-spor -- som tegnes uten fylling, ikke som en tom time. */
  minutter?: (number | null)[]
  /** Om timen lot seg måle. false skraverer merket. */
  opplost?: boolean[]
}

export function Dager({ rader, valgt, velg, vanlig, vindu }: {
  rader: DagRad[]; valgt: string | null; velg: (p: string) => void
  vanlig: number; vindu: Vindu
}) {
  const inne = useInne([rader.length])
  if (!rader.length) return null

  return (
    <div className="dager">
      {rader.map((r, i) => {
        const m = merker(r.okter, r.timer, vindu, 60,
                         dagStartAv(r.okter, r.period), r.baatTimer, r.minutter,
                         r.opplost)
        const paa = r.period === valgt
        const kort = !r.arbeid && r.hours != null && r.hours > 0
          && vanlig > 0 && r.hours < vanlig - 0.5
        const redusert = m.filter((x) => x.s === 1).length
        const blaas = m.reduce((a, x) => a + (x.fyll ?? 0) * 60, 0)
        return (
          <button key={r.period}
            className={`dag-rad${paa ? ' valgt' : ''}${r.paagaar ? ' paagaar' : ''}`}
            aria-pressed={paa}
            aria-label={`${r.period}: `
              + `${r.hours != null ? `${r.hours.toFixed(1)} timer` : 'ingen måling'}`
              + `${r.arbeid ? ', usikkert -- arbeid på anlegget' : ''}`
              + `${r.stopp.length ? `, ${r.stopp.length} avbrudd` : ''}`
              + `${redusert ? `, ${redusert} timer med redusert mengde` : ''}`
              + `${m.some((x) => x.baat) ? ', båt ved anlegget' : ''}`
              + `${r.paagaar ? ', måles fortsatt' : ''}`
              + `${blaas > 0 ? `, ${Math.round(blaas)} minutter blåsetid` : ''}`
              + `${m.some((x) => x.uvisst) ? ', noen timer lot seg ikke måle' : ''}`}
            onClick={() => velg(r.period)}
            style={{ opacity: inne ? 1 : 0,
                     transitionDelay: `${Math.min(i * 20, 340)}ms` }}>
            <span className="dag-rad-dato">{kortDato(r.period)}
              {r.paagaar && <i className="rad-naa" aria-hidden="true" />}</span>
            <span className="merkerad sm">
              {m.map((x, k) => (
                <span key={k} className={`merke-time s${x.s}${x.lys ? '' : ' natt'}`
                        + `${x.baat ? ' baat' : ''}`
                        + `${x.fyll === null ? '' : ' maalt'}`
                        + `${x.uvisst ? ' uvisst' : ''}`}>
                  {x.fyll !== null && (
                    <i style={{ height: `${Math.round(x.fyll * 100)}%` }} />
                  )}
                </span>
              ))}
            </span>
            <span className={`dag-rad-t${kort ? ' kort' : ''}`}>
              {r.hours != null && r.hours > 0
                ? `${r.hours.toFixed(1).replace('.', ',')}` : '–'}
              {r.arbeid && <i className="rad-arbeid" title="arbeid på anlegget" />}
            </span>
            <span className="dag-rad-avbrudd">
              {r.stopp.length ? `${r.stopp.length}` : ''}
            </span>
          </button>
        )
      })}
      <div className="dager-akse">
        <span />
        <span className="merker-akse sm">
          {Array.from({ length: vindu.toH - vindu.fromH + 1 }, (_, i) => {
            const h = vindu.fromH + i
            return (
              <span key={h}
                    style={{ left: `${((h - vindu.fromH) / (vindu.toH - vindu.fromH)) * 100}%` }}>
                {String(h).padStart(2, '0')}
              </span>
            )
          })}
        </span>
        <span /><span />
      </div>
    </div>
  )
}

export function kortDato(period: string): string {
  return new Date(`${period}T12:00:00Z`).toLocaleDateString('nb-NO',
    { day: 'numeric', month: 'short' })
}
